import React, { useCallback, useMemo } from 'react';
import { Container, SpaceBetween } from '@amzn/awsui-components-core';
import { PublicationSearchResultItem } from '@amzn/awscat-pattern-library-service-model';
import MetadataEditorInputV2 from '../MetadataEditorInputV2';
import constants from '../../../../common/constants';
import * as UtilApi from '../../../../api/UtilsApi';
import DuplicationCheck from '../../../utils/DuplicationCheck';
import '../../../../resources/styles/scss/components/create-content-wizard/Step3ContentDetails.css';

interface Step3ContentDetailsProps {
  readonly contentName: string;
  readonly setContentName: (value: string) => void;
  readonly contentShortName: string;
  readonly setContentShortName: (value: string) => void;
  readonly description: string;
  readonly setDescription: (value: string) => void;
  readonly proposal: string;
  readonly setProposal: (value: string) => void;
  readonly problemSolved: string;
  readonly setProblemSolved: (value: string) => void;
  readonly showValidationError?: boolean;
  readonly isSearchingDuplicates?: boolean;
  readonly duplicateContents?: PublicationSearchResultItem[];
  readonly numberOfDuplicatesToShow?: number;
  readonly onShowMoreDuplicates?: () => void;
}

interface FieldConfig {
  sectionId: string;
  className: string;
  value: string;
  label: string;
  description: string;
  contextualHelp: string;
  minLength: number;
  maxLength: number;
  isRequired: boolean;
  singleLine?: boolean;
}

const Step3ContentDetails: React.FC<Step3ContentDetailsProps> = ({
  contentName,
  setContentName,
  contentShortName,
  setContentShortName,
  description,
  setDescription,
  proposal,
  setProposal,
  problemSolved,
  setProblemSolved,
  showValidationError = false,
  isSearchingDuplicates = false,
  duplicateContents = [],
  numberOfDuplicatesToShow = UtilApi.defaultNumberOfDuplicatesToShow,
  onShowMoreDuplicates,
}) => {
  const handleInputUpdated = useCallback(
    (sectionId: string, value: string) => {
      const setters: Record<string, (v: string) => void> = {
        contentName: setContentName,
        contentShortName: setContentShortName,
        description: setDescription,
        proposal: setProposal,
        problemSolved: setProblemSolved,
      };
      setters[sectionId]?.(value);
    },
    [setContentName, setContentShortName, setDescription, setProposal, setProblemSolved],
  );

  const fieldConfigs: FieldConfig[] = useMemo(
    () => [
      {
        sectionId: 'contentName',
        className: 'create-content-name',
        value: contentName,
        label: constants.LONG_TITLE_LABEL,
        description: constants.CREATE_CONTENT_WIZARD_STEP3_LONG_TITLE_DESCRIPTION,
        contextualHelp: 'content-titles',
        minLength: UtilApi.longTitleInputMinLength,
        maxLength: UtilApi.longTitleInputMaxLength,
        isRequired: true,
        singleLine: true,
      },
      {
        sectionId: 'contentShortName',
        className: 'create-content-short-name',
        value: contentShortName,
        label: constants.SHORT_TITLE_LABEL,
        description: constants.CREATE_CONTENT_WIZARD_STEP3_SHORT_TITLE_DESCRIPTION,
        contextualHelp: 'content-titles',
        minLength: UtilApi.shortTitleInputMinLength,
        maxLength: UtilApi.shortTitleInputMaxLength,
        isRequired: true,
        singleLine: true,
      },
      {
        sectionId: 'description',
        className: 'create-content-description',
        value: description,
        label: constants.CONTENT_DESCRIPTION_LABEL,
        description: constants.CREATE_CONTENT_WIZARD_STEP3_OVERVIEW_DESCRIPTION,
        contextualHelp: 'content-overview',
        minLength: UtilApi.descriptionInputMinLength,
        maxLength: UtilApi.descriptionInputMaxLength,
        isRequired: true,
      },
      {
        sectionId: 'proposal',
        className: 'create-content-proposal',
        value: proposal,
        label: constants.CONTENT_PROPOSAL_LABEL,
        description: constants.CREATE_CONTENT_WIZARD_STEP3_PROPOSAL_DESCRIPTION,
        contextualHelp: 'content-proposal',
        minLength: UtilApi.proposalInputMinLength,
        maxLength: UtilApi.proposalInputMaxLength,
        isRequired: true,
      },
      {
        sectionId: 'problemSolved',
        className: 'create-content-problem-solved',
        value: problemSolved,
        label: constants.CONTENT_PROBLEM_SOLVED_LABEL,
        description: constants.CREATE_CONTENT_WIZARD_STEP3_PROBLEM_SOLVED_DESCRIPTION,
        contextualHelp: 'content-problem-solved',
        minLength: UtilApi.problemSolvedInputMinLength,
        maxLength: UtilApi.problemSolvedInputMaxLength,
        isRequired: true,
      },
    ],
    [contentName, contentShortName, description, proposal, problemSolved],
  );

  return (
    <Container>
      <SpaceBetween size="l">
        {fieldConfigs.map((field) => (
          <div key={field.sectionId} className={field.className}>
            <MetadataEditorInputV2
              sectionId={field.sectionId}
              value={field.value}
              label={field.label}
              description={field.description}
              contextualHelp={field.contextualHelp}
              onInputUpdated={handleInputUpdated}
              stretch
              isRequired={field.isRequired}
              showValidationError={showValidationError}
              minLength={field.minLength}
              maxLength={field.maxLength}
              removeFormatting
              singleLine={field.singleLine}
              hideMenuBar
              hideFooter
            />
            {field.sectionId === 'contentName' && (
              <div className="wizard-duplicate-check">
                <DuplicationCheck
                  loadingMessage="Searching for similar titles..."
                  noDuplicatesMessage="No similar titles found."
                  matchFoundMessage={(count) =>
                    `We found ${count} APG publication${count > 1 ? 's' : ''} with a similar title.`
                  }
                  duplicateContentMessage="Please review the following to confirm that your topic and title are unique:"
                  compactLayout
                  contentName={contentName}
                  isSearchingDuplicates={isSearchingDuplicates}
                  duplicateContents={duplicateContents}
                  numberOfDuplicatesToShow={numberOfDuplicatesToShow}
                  handleShowMoreDuplicatesClick={onShowMoreDuplicates ?? (() => {})}
                />
              </div>
            )}
          </div>
        ))}
      </SpaceBetween>
    </Container>
  );
};

export default Step3ContentDetails;
